import {
  defaultTypography,
  genPageTheme,
  palettes,
  shapes,
  createUnifiedTheme,
} from '@backstage/theme';
import tokens from "@cfa/design-tokens";

const apercuRegular = {
  fontFamily: 'Apercu',
  fontDisplay: 'swap',
  fontWeight: '300',
  src: `
    local('Apercu Regular'),
    url(${"../../apercu-regular.otf"}) format('opentype'),
  `,
}

const apercuMedium = {
  fontFamily: 'Apercu',
  fontDisplay: 'swap',
  fontWeight: '500',
  src: `
    local('Apercu Medium'),
    url(${"../../apercu-medium.otf"}) format('opentype'),
  `,
}

const apercuBold = {
  fontFamily: 'Apercu',
  fontDisplay: 'swap',
  fontWeight: '600 700',
  src: `
    local('Apercu Bold'),
    url(${"../../apercu-bold.otf"}) format('opentype'),
  `,
}

const apercuExtraBold = {
  fontFamily: 'Apercu',
  fontDisplay: 'swap',
  fontWeight: '800 900',
  src: `
    local('Apercu ExtraBold'),
    url(${"../../apercu-extrabold.otf"}) format('opentype'),
  `,
}

const apercuExtraLight = {
  fontFamily: 'Apercu',
  fontDisplay: 'swap',
  fontWeight: '200 300',
  src: `
    local('Apercu ExtraLight'),
    url(${"../../apercu-extralight.otf"}) format('opentype'),
  `,
}

export const cfaLightTheme = createUnifiedTheme({
  fontFamily: 'Apercu',
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [
          apercuExtraLight,
          apercuRegular,
          apercuMedium,
          apercuBold,
          apercuExtraBold,
        ],
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: `${tokens.color.brand.warm_white.modes.light}`,
          },
        },
        selected: {
          backgroundColor: tokens.color.primary.base.modes.light,
        },
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        item: {
          '& svg': {
            flexShrink: 0,
            marginRight: '8px',
          },
        },
      },
    },
    BackstageIconLinkVertical: {
      styleOverrides: {
        disabled: {
          color: tokens.color.disabled.contrast.modes.light,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: {
          width: '100%',
          boxSizing: 'border-box',
        },
      },
    },
    BackstageSidebarPage: {
      styleOverrides: {
        root: {
          overflowX: 'hidden',
        },
      },
    },
    BackstagePage: {
      styleOverrides: {
        root: {
          width: '100%',
          minWidth: 0,
        },
      },
    },
  },

  typography: {
    ...defaultTypography,
    htmlFontSize: 14,
    fontFamily: 'Apercu'
  },
  palette: {
    ...palettes.light,
    background: {
      default: tokens.color.base_0.modes.light,
      paper: tokens.color.base_2.modes.light,
    },
    // primary is links, and the red from tokens.color.primary is too loud for the amount of
    // links we have, so we switch secondary and primary, effectively giving us a blue-first color
    // palette
    primary: {
      main: tokens.color.secondary.base.modes.light,
      contrastText: tokens.color.secondary.contrast.modes.light,
    },
    secondary: {
      main: tokens.color.primary.base.modes.light,
      contrastText: tokens.color.primary.contrast.modes.light,
    },

    navigation: {
      submenu: {
        background: tokens.color.nav.primary_active.modes.light,
      },
      background: tokens.color.nav.primary_active.modes.light,
      navItem: {
        hoverBackground: tokens.color.primary.hover.modes.light,
      },
      indicator: tokens.color.primary.base.modes.light,
      selectedColor: tokens.color.brand.warm_white.modes.light,
      color: tokens.color.secondary.base.modes.light,
    },
    border: tokens.color.base_2.modes.light,
    divider: tokens.color.divider.modes.light,
    error: {
      main: tokens.color.feedback.error.base.modes.light,
      contrastText: tokens.color.feedback.error.contrast.modes.light,
    },
    warning: {
      main: tokens.color.feedback.warning.base.modes.light,
      contrastText: tokens.color.feedback.warning.contrast.modes.light,
    },
    success: {
      main: tokens.color.feedback.success.base.modes.light,
      contrastText: tokens.color.feedback.success.contrast.modes.light,
    },
    info: {
      main: tokens.color.feedback.info.base.modes.light,
      contrastText: tokens.color.feedback.info.contrast.modes.light,
    },
    text: {
      primary: tokens.color.contrast.modes.light,
      secondary: tokens.color.secondary.base.modes.light,
      disabled: tokens.color.disabled.contrast.modes.light,
    },
  },
  /**
   * The colors in page theme are used to generate the background and container colors for
   * the banners on each page
   */
  pageTheme: {
    home: genPageTheme({
      colors: [tokens.color.primary.base.modes.light, tokens.color.brand.dark_gray.modes.light],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [tokens.color.secondary.base.modes.light, tokens.color.secondary.hover.modes.light],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [tokens.color.secondary.base.modes.light, tokens.color.brand.blue.modes.light],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [tokens.color.brand.dark_red.modes.light, tokens.color.brand.warm_white.modes.light],
      shape: shapes.wave,
    }),
    // API explorer page only
    apis: genPageTheme({
      colors: [tokens.color.secondary.base.modes.light, tokens.color.brand.light_blue.modes.light],
      shape: shapes.wave,
    }),
    tool: genPageTheme({
      colors: [tokens.color.secondary.base.modes.light, tokens.color.brand.dark_red.modes.light],
      shape: shapes.wave2,
    }),
  },
  defaultPageTheme: 'home',

});

export const cfaDarkTheme = createUnifiedTheme({
  fontFamily: 'Apercu',
  components: {
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: `${tokens.color.brand.warm_white.modes.dark}`,
          },
        },
        selected: {
          backgroundColor: tokens.color.secondary.base.modes.dark,
        },
      },
    },
    BackstageSidebarSubmenu: {
      styleOverrides:{
        root: {
          '&:hover': {
            color: `${tokens.color.brand.warm_white.modes.dark}`,
          },
        },
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        item: {
          '& svg': {
            flexShrink: 0,
            marginRight: '8px',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [apercuRegular, apercuMedium, apercuBold, apercuExtraBold, apercuExtraLight],
      }
    }
  },
  typography: {
    ...defaultTypography,
    htmlFontSize: 14,
    fontFamily: 'Apercu'
  },
  palette: {
    ...palettes.dark,
    background: {
      default: tokens.color.base_1.modes.dark,
      paper: tokens.color.base_2.modes.dark,
    },
    // primary is links, and the red from tokens.color.primary is too loud for the amount of
    // links we have, so we switch secondary and primary, effectively giving us a blue-first color
    // palette. Using a brighter custom blue for better contrast against dark backgrounds for links
    primary: {
      main: '#5DADE2', // Custom bright blue for better link visibility on dark backgrounds
      contrastText: tokens.color.secondary.contrast.modes.dark,
    },
    secondary: {
      main: tokens.color.primary.base.modes.dark,
      contrastText: tokens.color.primary.contrast.modes.dark,
    },

    navigation: {
      submenu: {
        background: tokens.color.base_2.modes.dark,
      },
      background: tokens.color.base_2.modes.dark,
      navItem: {
        hoverBackground: tokens.color.secondary.hover.modes.dark,
      },
      indicator: tokens.color.secondary.base_alt.modes.dark,
      selectedColor: tokens.color.brand.warm_white.modes.dark,
      color: tokens.color.brand.light_blue.modes.dark,
    },
    border: tokens.color.base_2.modes.dark,
    divider: tokens.color.divider.modes.dark,
    error: {
      main: tokens.color.feedback.error.base.modes.dark,
      contrastText: tokens.color.feedback.error.contrast.modes.dark,
    },
    warning: {
      main: tokens.color.feedback.warning.base.modes.dark,
      contrastText: tokens.color.feedback.warning.contrast.modes.dark,
    },
    success: {
      main: tokens.color.feedback.success.base.modes.dark,
      contrastText: tokens.color.feedback.success.contrast.modes.dark,
    },
    info: {
      main: tokens.color.feedback.info.base.modes.dark,
      contrastText: tokens.color.feedback.info.contrast.modes.dark,
    },
    text: {
      primary: tokens.color.contrast.modes.dark,
      secondary: tokens.color.brand.light_blue.modes.dark,
      disabled: tokens.color.disabled.contrast.modes.dark,
    },
  },
  pageTheme: {
    home: genPageTheme({
      colors: [tokens.color.primary.base.modes.dark, tokens.color.brand.dark_gray.modes.dark],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [tokens.color.secondary.base.modes.dark, tokens.color.secondary.hover.modes.dark],
      shape: shapes.wave2,
    }),
    service: genPageTheme({
      colors: [tokens.color.secondary.base.modes.dark, tokens.color.brand.blue.modes.dark],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [tokens.color.brand.dark_red.modes.dark, tokens.color.brand.warm_white.modes.dark],
      shape: shapes.wave,
    }),
    // API explorer page only
    apis: genPageTheme({
      colors: [tokens.color.secondary.base.modes.dark, tokens.color.brand.light_blue.modes.dark],
      shape: shapes.wave,
    }),
    tool: genPageTheme({
      colors: [tokens.color.secondary.base.modes.dark, tokens.color.brand.dark_red.modes.dark],
      shape: shapes.wave2,
    }),
  },
  defaultPageTheme: 'home',
}); 